/**
 * Frame statistics probe (docs/large-volume-plan.md §2.7, step S0).
 *
 * Records, per rendered frame: CPU frame ms (R3F before-effect to after-effect, which spans the
 * coordinator flush in VoxelRendererFrame and the renderer.render call), draw calls and triangles
 * from `renderer.info`, GPU ms (WebGPU timestamp queries when the backend tracks them, otherwise the
 * `queue.onSubmittedWorkDone` latency, reported under a separate name), and input-to-present ms for
 * frames that followed a stroke dispatch (`markStrokeInputForEvents` from processEventsBatch).
 *
 * The probe is passive: it never calls `invalidate`, never adds a `useFrame`, and does nothing unless
 * the `huebo:frame-stats` flag is '1'. The R3F global effect registrars are passed in by the caller
 * (HueboCanvas) so this module has no renderer or React dependency and the collector can be unit
 * tested without a GPU.
 */

export const FRAME_STATS_STORAGE_KEY = 'huebo:frame-stats'

export type FrameStatsMetric =
  | 'cpuMs'
  | 'gpuMs'
  | 'latencyMs'
  | 'inputToPresentMs'
  | 'drawCalls'
  | 'triangles'

export const FRAME_STATS_METRICS: readonly FrameStatsMetric[] = [
  'cpuMs',
  'gpuMs',
  'latencyMs',
  'inputToPresentMs',
  'drawCalls',
  'triangles',
]

export type FrameSample = {
  /** Frame index since the collector was created or reset. */
  index: number
  /** performance.now() at the before-effect. */
  start: number
  cpuMs: number
  drawCalls: number | null
  triangles: number | null
  gpuMs: number | null
  latencyMs: number | null
  inputToPresentMs: number | null
}

export type MetricSummary = {
  count: number
  p50: number
  p95: number
  max: number
  mean: number
}

export type FrameStatsSummary = {
  frames: number
  /** Wall time between the first and last recorded frame start (ms); 0 for fewer than two frames. */
  spanMs: number
  gpuTimingSource: GpuTimingSource
  /** Stroke inputs marked that have not yet been attributed to a frame. */
  pendingInputs: number
  metrics: Record<FrameStatsMetric, MetricSummary | null>
}

export type GpuTimingSource = 'timestamp-query' | 'latency' | 'none'

// ----------------------------------------------------------------------------
// Ring buffer + percentile math (pure; unit tested)
// ----------------------------------------------------------------------------

export class RingBuffer<T> {
  private readonly items: (T | undefined)[]
  private head = 0
  private count = 0

  constructor(readonly capacity: number) {
    if (!(capacity > 0)) throw new Error('RingBuffer capacity must be positive')
    this.items = new Array(capacity)
  }

  get length(): number {
    return this.count
  }

  push(item: T): void {
    this.items[(this.head + this.count) % this.capacity] = item
    if (this.count < this.capacity) {
      this.count++
    } else {
      this.head = (this.head + 1) % this.capacity
    }
  }

  /** Oldest to newest. */
  toArray(): T[] {
    const out: T[] = new Array(this.count)
    for (let i = 0; i < this.count; i++) {
      out[i] = this.items[(this.head + i) % this.capacity] as T
    }
    return out
  }

  /** Removes and returns the oldest item, or undefined when empty. */
  shift(): T | undefined {
    if (this.count === 0) return undefined
    const item = this.items[this.head]
    this.items[this.head] = undefined
    this.head = (this.head + 1) % this.capacity
    this.count--
    return item
  }

  peek(): T | undefined {
    return this.count === 0 ? undefined : this.items[this.head]
  }

  clear(): void {
    this.items.fill(undefined)
    this.head = 0
    this.count = 0
  }
}

/** Nearest-rank percentile over a sorted ascending array (p in [0, 1]). */
export function percentileSorted(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return NaN
  const rank = Math.ceil(Math.min(1, Math.max(0, p)) * sorted.length)
  return sorted[Math.max(0, Math.min(sorted.length - 1, rank - 1))]
}

export function summarizeValues(values: readonly number[]): MetricSummary | null {
  const finite = values.filter((v) => Number.isFinite(v))
  if (finite.length === 0) return null
  const sorted = [...finite].sort((a, b) => a - b)
  let sum = 0
  for (const v of sorted) sum += v
  return {
    count: sorted.length,
    p50: percentileSorted(sorted, 0.5),
    p95: percentileSorted(sorted, 0.95),
    max: sorted[sorted.length - 1],
    mean: sum / sorted.length,
  }
}

// ----------------------------------------------------------------------------
// Collector (pure; drives the sample ring from begin/end frame calls)
// ----------------------------------------------------------------------------

export type FrameInfo = {
  drawCalls?: number | null
  triangles?: number | null
}

export type FrameStatsCollectorOptions = {
  /** Frames kept for summaries. */
  capacity?: number
  /** Pending stroke inputs kept before the oldest is dropped. */
  inputCapacity?: number
}

export class FrameStatsCollector {
  private readonly samples: RingBuffer<FrameSample>
  private readonly pendingInputs: RingBuffer<number>
  private frameStart: number | null = null
  private nextIndex = 0
  gpuTimingSource: GpuTimingSource = 'none'

  constructor(opts: FrameStatsCollectorOptions = {}) {
    this.samples = new RingBuffer<FrameSample>(opts.capacity ?? 600)
    this.pendingInputs = new RingBuffer<number>(opts.inputCapacity ?? 256)
  }

  /** Called when a stroke event batch is dispatched. */
  markInput(t: number): void {
    this.pendingInputs.push(t)
  }

  get pendingInputCount(): number {
    return this.pendingInputs.length
  }

  beginFrame(t: number): void {
    this.frameStart = t
  }

  /**
   * Closes the frame started by `beginFrame` and returns the sample, or null when no frame was open.
   * Every stroke input marked before this frame started is attributed to it: the frame's
   * input-to-present value is the latency of the oldest such input (the worst case).
   */
  endFrame(t: number, info: FrameInfo = {}): FrameSample | null {
    const start = this.frameStart
    if (start === null) return null
    this.frameStart = null

    let oldestInput: number | null = null
    while (this.pendingInputs.length > 0) {
      const next = this.pendingInputs.peek() as number
      if (next > start) break
      this.pendingInputs.shift()
      if (oldestInput === null) oldestInput = next
    }

    const sample: FrameSample = {
      index: this.nextIndex++,
      start,
      cpuMs: t - start,
      drawCalls: numberOrNull(info.drawCalls),
      triangles: numberOrNull(info.triangles),
      gpuMs: null,
      latencyMs: null,
      inputToPresentMs: oldestInput === null ? null : t - oldestInput,
    }
    this.samples.push(sample)
    return sample
  }

  /** Attaches a GPU duration (ms) to a sample, typically after an async readback. */
  recordGpu(sample: FrameSample, gpuMs: number): void {
    if (Number.isFinite(gpuMs)) sample.gpuMs = gpuMs
  }

  /** Attaches a queue-completion latency (ms) to a sample. */
  recordLatency(sample: FrameSample, latencyMs: number): void {
    if (Number.isFinite(latencyMs)) sample.latencyMs = latencyMs
  }

  frames(): FrameSample[] {
    return this.samples.toArray()
  }

  get frameCount(): number {
    return this.samples.length
  }

  reset(): void {
    this.samples.clear()
    this.pendingInputs.clear()
    this.frameStart = null
    this.nextIndex = 0
  }

  summary(): FrameStatsSummary {
    const frames = this.samples.toArray()
    const metrics = {} as Record<FrameStatsMetric, MetricSummary | null>
    for (const metric of FRAME_STATS_METRICS) {
      const values: number[] = []
      for (const f of frames) {
        const v = f[metric]
        if (v !== null && v !== undefined) values.push(v)
      }
      metrics[metric] = summarizeValues(values)
    }
    const spanMs = frames.length >= 2 ? frames[frames.length - 1].start - frames[0].start : 0
    return {
      frames: frames.length,
      spanMs,
      gpuTimingSource: this.gpuTimingSource,
      pendingInputs: this.pendingInputs.length,
      metrics,
    }
  }
}

function numberOrNull(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

// ----------------------------------------------------------------------------
// Stroke input marks (called from the dispatch path; cheap when no probe is installed)
// ----------------------------------------------------------------------------

let activeCollector: FrameStatsCollector | null = null

const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now())
/** The installed probe's clock (overridable in tests), shared by the input marks. */
let activeClock: () => number = now

/** Records the time a stroke batch entered the dispatch path. No-op without an installed probe. */
export function markStrokeInput(t: number = activeClock()): void {
  activeCollector?.markInput(t)
}

function isStrokeEventType(type: unknown): boolean {
  return typeof type === 'string' && (type.startsWith('scribble.') || type.startsWith('brush.stroke'))
}

/** One-line hook for processEventsBatch: marks the batch when it carries a scribble or brush event. */
export function markStrokeInputForEvents(events: ReadonlyArray<readonly unknown[]>): void {
  if (!activeCollector) return
  for (const ev of events) {
    if (isStrokeEventType(ev[0])) {
      activeCollector.markInput(activeClock())
      return
    }
  }
}

// ----------------------------------------------------------------------------
// Probe installation
// ----------------------------------------------------------------------------

export function isFrameStatsEnabled(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(FRAME_STATS_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function writeFrameStatsEnabled(enabled: boolean): void {
  try {
    if (typeof localStorage === 'undefined') return
    if (enabled) localStorage.setItem(FRAME_STATS_STORAGE_KEY, '1')
    else localStorage.removeItem(FRAME_STATS_STORAGE_KEY)
  } catch {
    // ignore
  }
}

type EffectRegistrar = (callback: (timestamp: number) => void) => () => void

/** The slice of three's WebGPURenderer the probe touches (kept structural so tests need no renderer). */
export type ProbeRenderer = {
  info?: { render?: { drawCalls?: number; triangles?: number; timestamp?: number } }
  backend?: {
    trackTimestamp?: boolean
    hasFeature?: (name: string) => boolean
    device?: { queue?: { onSubmittedWorkDone?: () => Promise<unknown> } }
  }
  resolveTimestampsAsync?: (type?: string) => Promise<number | undefined>
}

export type FrameStatsProbeOptions = {
  /** R3F `addEffect` (runs before each rendered frame). */
  addEffect: EffectRegistrar
  /** R3F `addAfterEffect` (runs after each rendered frame). */
  addAfterEffect: EffectRegistrar
  capacity?: number
  /** Clock override for tests. */
  now?: () => number
  /** When false, never enable WebGPU timestamp queries (forces the latency fallback). */
  allowTimestampQueries?: boolean
}

export type FrameStatsApi = {
  readonly frames: number
  readonly gpuTimingSource: GpuTimingSource
  summary: () => FrameStatsSummary
  samples: () => FrameSample[]
  reset: () => void
  /** Human-readable one-liner of the summary. */
  format: () => string
}

let uninstallActive: (() => void) | null = null

/**
 * `huebo:frame-stats-gpu` = `latency`: report `queue.onSubmittedWorkDone` latency even where timestamp
 * queries exist. Render-pass timestamps miss compute submitted outside the render (the SDF surface
 * passes); the latency covers everything queued up to the frame's submit.
 */
function preferLatencyTiming(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('huebo:frame-stats-gpu') === 'latency'
  } catch {
    return false
  }
}

/**
 * Installs the probe on the R3F global effect hooks. Returns an uninstall function. Installing again
 * replaces the previous probe. Exposes `window.__hueboFrameStats`.
 */
export function installFrameStatsProbe(
  getRenderer: () => ProbeRenderer | null,
  opts: FrameStatsProbeOptions,
): () => void {
  uninstallActive?.()

  const clock = opts.now ?? now
  const collector = new FrameStatsCollector({ capacity: opts.capacity })
  activeCollector = collector
  activeClock = clock

  let timestampQueriesReady = false
  let timestampSetupDone = false
  const setupTimestamps = (renderer: ProbeRenderer): void => {
    if (timestampSetupDone) return
    timestampSetupDone = true
    const backend = renderer.backend
    if (!backend) {
      collector.gpuTimingSource = 'none'
      return
    }
    if (opts.allowTimestampQueries !== false && !preferLatencyTiming() && typeof backend.hasFeature === 'function') {
      try {
        if (backend.hasFeature('timestamp-query') && typeof renderer.resolveTimestampsAsync === 'function') {
          // three 0.181: `trackTimestamp` is a constructor parameter, but the backend only gates the
          // lazily created query pool on this flag, so enabling it after init works.
          backend.trackTimestamp = true
          timestampQueriesReady = true
          collector.gpuTimingSource = 'timestamp-query'
          return
        }
      } catch {
        // fall through to latency
      }
    }
    collector.gpuTimingSource = backend.device?.queue?.onSubmittedWorkDone ? 'latency' : 'none'
  }

  let resolving = false
  const removeBefore = opts.addEffect(() => {
    collector.beginFrame(clock())
  })
  const removeAfter = opts.addAfterEffect(() => {
    const renderer = getRenderer()
    const info = renderer?.info?.render
    const sample = collector.endFrame(clock(), {
      drawCalls: info?.drawCalls,
      triangles: info?.triangles,
    })
    if (!sample || !renderer) return
    setupTimestamps(renderer)

    if (timestampQueriesReady) {
      if (resolving) return
      resolving = true
      // Resolving each frame keeps the query pool from filling (2048 queries) and gives per-frame values.
      void renderer.resolveTimestampsAsync!('render')
        .then((duration) => {
          if (typeof duration === 'number') collector.recordGpu(sample, duration)
        })
        .catch(() => undefined)
        .finally(() => {
          resolving = false
        })
      return
    }

    const queue = renderer.backend?.device?.queue
    if (queue?.onSubmittedWorkDone) {
      const submitted = clock()
      try {
        void queue
          .onSubmittedWorkDone()
          .then(() => collector.recordLatency(sample, clock() - submitted))
          .catch(() => undefined)
      } catch {
        // ignore
      }
    }
  })

  const api: FrameStatsApi = {
    get frames() {
      return collector.frameCount
    },
    get gpuTimingSource() {
      return collector.gpuTimingSource
    },
    summary: () => collector.summary(),
    samples: () => collector.frames(),
    reset: () => collector.reset(),
    format: () => formatFrameStatsSummary(collector.summary()),
  }

  const g = globalThis as unknown as { __hueboFrameStats?: FrameStatsApi }
  g.__hueboFrameStats = api

  const uninstall = () => {
    removeBefore()
    removeAfter()
    if (activeCollector === collector) {
      activeCollector = null
      activeClock = now
    }
    if (g.__hueboFrameStats === api) delete g.__hueboFrameStats
    if (uninstallActive === uninstall) uninstallActive = null
  }
  uninstallActive = uninstall
  return uninstall
}

/** The installed probe's API, if any (same object as `window.__hueboFrameStats`). */
export function getFrameStatsApi(): FrameStatsApi | null {
  const g = globalThis as unknown as { __hueboFrameStats?: FrameStatsApi }
  return g.__hueboFrameStats ?? null
}

export function formatFrameStatsSummary(s: FrameStatsSummary): string {
  const parts: string[] = [`${s.frames} frames`]
  if (s.spanMs > 0 && s.frames > 1) parts.push(`${(1000 * (s.frames - 1) / s.spanMs).toFixed(1)} fps`)
  const fmt = (name: string, m: MetricSummary | null, digits = 2) =>
    m ? `${name} p50 ${m.p50.toFixed(digits)} p95 ${m.p95.toFixed(digits)} max ${m.max.toFixed(digits)}` : null
  for (const [name, digits] of [
    ['cpuMs', 2],
    ['gpuMs', 2],
    ['latencyMs', 2],
    ['inputToPresentMs', 2],
    ['drawCalls', 0],
    ['triangles', 0],
  ] as const) {
    const line = fmt(name, s.metrics[name], digits)
    if (line) parts.push(line)
  }
  parts.push(`gpu source ${s.gpuTimingSource}`)
  return parts.join(' | ')
}
